import { Alert, Button, Form, Input, InputNumber, message, Table, Tag } from "antd";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { FinanceEntryType } from "../typeDefs";
import { isInGstRegisteredPeriod } from "./taxConfig";

const money = (value: number) => `$${(value || 0).toFixed(2)}`;
const gstPortion = (amount: number) => (amount * 3) / 23;

const getGstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstIncluded || !entry.gstRegistered) return 0;
  const gst = gstPortion(entry.amount);
  if (entry.category === "Expenses" || entry.category === "Assets") {
    const apportionRate = entry.apportionMethod === "None" ? 0 : entry.apportionRate ?? 100;
    return gst * (apportionRate / 100);
  }
  return gst;
};

type TargetRecord = {
  id: string;
  businessIncome?: number;
  lossBroughtForward?: number;
  taxableIncome?: number;
  shareholderSalary?: number;
  currentAccountBalance?: number;
  gstPeriodStart?: number;
  gstPeriodEnd?: number;
  gstSales?: number;
  gstCollected?: number;
  gstPurchases?: number;
  gstClaimable?: number;
  gstToPay?: number;
  personalTaxableIncome?: number;
  personalTerminalTax?: number;
  notes?: string;
  updatedAt?: number;
};

const defaultTargets: Record<string, Partial<TargetRecord>> = {
  "2023-2024": {
    businessIncome: -6299,
    lossBroughtForward: -1452,
    taxableIncome: -7751,
    shareholderSalary: 0,
    currentAccountBalance: 12382,
    notes: "From 2024 IR4 Tax Summary: loss carried forward to 2025 $7,751, current account $12,382.",
  },
  "2024-2025": {
    businessIncome: 7751,
    lossBroughtForward: -7751,
    taxableIncome: 0,
    shareholderSalary: 33567,
    currentAccountBalance: 14990,
    gstPeriodStart: new Date("2024-10-01").getTime(),
    gstPeriodEnd: new Date("2025-04-01").getTime(),
    gstSales: 30532.33,
    gstCollected: 3982.48,
    gstPurchases: 10664.79,
    gstClaimable: 1391.06,
    gstToPay: 2591.42,
    personalTaxableIncome: 33568.54,
    personalTerminalTax: 4819.01,
    notes: "From 2025 IR4 Tax Summary, Phil personal tax summary, and GST return 01/10/2024-31/03/2025.",
  },
  "2025-2026": {
    gstPeriodStart: new Date("2025-04-01").getTime(),
    gstPeriodEnd: new Date("2025-10-01").getTime(),
    gstSales: 28461.45,
    gstCollected: 3712.36,
    gstPurchases: 8645.16,
    gstClaimable: 1127.62,
    gstToPay: 2584.74,
    notes: "From signed GST101A for 01/04/2025-30/09/2025. Annual company and personal-tax figures remain provisional pending accountant review.",
  },
};

const getTaxYearId = (dateRange: number[]) => {
  const start = new Date(dateRange[0]);
  const end = new Date(dateRange[1]);
  return `${start.getFullYear()}-${end.getFullYear()}`;
};

export const AccountantAlignment = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const [form] = Form.useForm();
  const [savedTarget, setSavedTarget] = useState<TargetRecord | null>(null);
  const targetId = getTaxYearId(dateRange);
  const defaults = useMemo(() => defaultTargets[targetId] || {}, [targetId]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, `accountantAlignmentTargets/${targetId}`), (snapshot) => {
      const next = snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as TargetRecord) : null;
      setSavedTarget(next);
      form.setFieldsValue({ ...defaults, ...next });
    });
    return unsub;
  }, [defaults, form, targetId]);

  const entries = useMemo(
    () =>
      data
        .filter((item) => !item.isDeleted)
        .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]),
    [data, dateRange],
  );

  const values = Form.useWatch([], form) || {};
  const target = { ...defaults, ...savedTarget, ...values } as TargetRecord;

  const revenue = entries.filter((item) => item.category === "Revenue").reduce((total, item) => total + item.amount, 0);
  const salary = entries
    .filter(
      (item) =>
        (item.category === "Expenses" && ["Salaries and Wages", "Shareholder Salary"].includes(item.subCategory)) ||
        (item.category === "Equity" &&
          item.subCategory === "Shareholder Drawings" &&
          item.yearEndTreatment === "Shareholder Salary"),
    )
    .reduce((total, item) => total + item.amount, 0);
  const expensesExSalary = entries
    .filter((item) => item.category === "Expenses" && !["Salaries and Wages", "Shareholder Salary"].includes(item.subCategory))
    .reduce((total, item) => total + item.amount, 0);
  const appProfitBeforeAdjustments = revenue - salary - expensesExSalary;
  const adjustmentNeeded = (target.businessIncome ?? 0) - appProfitBeforeAdjustments;

  const gstReturnStart = target.gstPeriodStart ?? dateRange[0];
  const gstReturnEnd = target.gstPeriodEnd ?? dateRange[1];
  const gstPeriodLabel = `${new Date(gstReturnStart).toLocaleDateString("en-NZ")}–${new Date(
    gstReturnEnd - 1,
  ).toLocaleDateString("en-NZ")}`;
  const gstReturnEntries = data
    .filter((item) => !item.isDeleted)
    .filter((item) => item.date >= gstReturnStart && item.date < gstReturnEnd);
  const gstSales = gstReturnEntries.filter((item) => item.category === "Revenue").reduce((total, item) => total + item.amount, 0);
  const gstCollected = gstReturnEntries
    .filter((item) => item.category === "Revenue")
    .reduce((total, item) => total + getGstAmount(item), 0);
  const gstPurchases = gstReturnEntries
    .filter((item) => item.category === "Expenses" || item.category === "Assets")
    .reduce((total, item) => total + item.amount, 0);
  const gstClaimable = gstReturnEntries
    .filter((item) => item.category === "Expenses" || item.category === "Assets")
    .reduce((total, item) => total + getGstAmount(item), 0);
  const gstToPay = gstCollected - gstClaimable;

  const rows = [
    { key: "revenue", label: "App gross revenue", app: revenue, target: null, variance: null, note: "Compare source income coding; app includes GST-inclusive receipts." },
    { key: "salary", label: "Shareholder salary / drawings treated as salary", app: salary, target: target.shareholderSalary, variance: salary - (target.shareholderSalary ?? 0), note: "Accountant 2025 target is $33,567; app currently needs extra salary/draw classification if variance remains." },
    { key: "profit", label: "Business income/(loss)", app: appProfitBeforeAdjustments, target: target.businessIncome, variance: appProfitBeforeAdjustments - (target.businessIncome ?? 0), note: "Variance is the accountant adjustment needed for depreciation, GST/accounting basis, private portions, and year-end journals." },
    { key: "loss", label: "Loss brought forward", app: 0, target: target.lossBroughtForward, variance: 0 - (target.lossBroughtForward ?? 0), note: "Carry forward from signed IR4 summaries; should feed tax carry-over records." },
    { key: "taxable", label: "Taxable income/(loss after carry-forward)", app: appProfitBeforeAdjustments + (target.lossBroughtForward ?? 0), target: target.taxableIncome, variance: appProfitBeforeAdjustments + (target.lossBroughtForward ?? 0) - (target.taxableIncome ?? 0), note: "Final IR4 taxable position after losses and adjustments." },
    { key: "current", label: "Shareholder current account", app: 0, target: target.currentAccountBalance, variance: 0 - (target.currentAccountBalance ?? 0), note: "Needs a dedicated current-account ledger/reconciliation against accountant balance." },
    { key: "gstSales", label: `GST return sales ${gstPeriodLabel}`, app: gstSales, target: target.gstSales, variance: gstSales - (target.gstSales ?? 0), note: "Box 5/7 on GST return." },
    { key: "gstCollected", label: "GST collected", app: gstCollected, target: target.gstCollected, variance: gstCollected - (target.gstCollected ?? 0), note: "Box 10 on GST return." },
    { key: "gstPurchases", label: "GST return purchases", app: gstPurchases, target: target.gstPurchases, variance: gstPurchases - (target.gstPurchases ?? 0), note: "Box 11 on GST return." },
    { key: "gstClaimable", label: "GST claimable", app: gstClaimable, target: target.gstClaimable, variance: gstClaimable - (target.gstClaimable ?? 0), note: "Box 14 on GST return." },
    { key: "gstToPay", label: "GST to pay", app: gstToPay, target: target.gstToPay, variance: gstToPay - (target.gstToPay ?? 0), note: "Box 15 on GST return." },
  ];

  const saveTargets = async () => {
    try {
      const formValues = await form.validateFields();
      await setDoc(doc(db, `accountantAlignmentTargets/${targetId}`), {
        id: targetId,
        ...formValues,
        updatedAt: Date.now(),
      } as TargetRecord, { merge: true });
      message.success("Accountant alignment targets saved.");
    } catch (err) {
      console.error(err);
      message.error("Could not save accountant alignment targets.");
    }
  };

  const hasMajorVariance = rows.some((row) => typeof row.variance === "number" && Math.abs(row.variance) > 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Alert
        type={hasMajorVariance ? "warning" : "success"}
        showIcon
        message={`Accountant alignment for ${targetId}`}
        description="Targets are seeded from the signed accountant/IRD documents you provided. Edit targets if the accountant updates them, then use the variance table to decide which entries/journals need correction."
      />

      <Form form={form} layout="vertical" initialValues={{ ...defaults, ...savedTarget }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Form.Item name="businessIncome" label="Business income/(loss)"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="lossBroughtForward" label="Loss brought forward"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="taxableIncome" label="Taxable income"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="shareholderSalary" label="Shareholder salary"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="currentAccountBalance" label="Current account balance"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="personalTaxableIncome" label="Personal taxable income"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="personalTerminalTax" label="Personal terminal tax"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="gstSales" label={`GST sales (${gstPeriodLabel})`}><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="gstCollected" label="GST collected"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="gstPurchases" label="GST purchases"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="gstClaimable" label="GST claimable"><InputNumber style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="gstToPay" label="GST to pay"><InputNumber style={{ width: "100%" }} /></Form.Item>
        </div>
        <Form.Item name="notes" label="Source notes"><Input.TextArea rows={3} /></Form.Item>
        <Button type="primary" onClick={saveTargets}>Save alignment targets</Button>
      </Form>

      <Table
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: "Metric", dataIndex: "label" },
          { title: "App", dataIndex: "app", render: (value) => typeof value === "number" ? money(value) : "-" },
          { title: "Accountant target", dataIndex: "target", render: (value) => typeof value === "number" ? money(value) : "-" },
          {
            title: "Variance",
            dataIndex: "variance",
            render: (value) =>
              typeof value === "number" ? <Tag color={Math.abs(value) > 1 ? "orange" : "green"}>{money(value)}</Tag> : "-",
          },
          { title: "What to check", dataIndex: "note" },
        ]}
      />

      <Alert
        type="info"
        showIcon
        message="Immediate alignment actions from the documents"
        description={`App salary/drawings marked as year-end salary are ${money(salary)} vs accountant target ${money(target.shareholderSalary || 0)}. Business income needs an adjustment of ${money(adjustmentNeeded)} from the app pre-adjustment figure to match the saved target. GST is compared with ${gstPeriodLabel}, not automatically with the full tax year.`}
      />
    </div>
  );
};
